import { TrashBin } from 'flowbite-react-icons/outline'

import { ComparatorDetailView } from '@/components/order/ComparatorDetailView'
import type { ComparatorParentConsentConfigSlice } from '@/components/order/comparatorParentConsentConfig'
import type { Comparator } from '@/ordering/canonicalOrder'
import { useComparatorSampleCollected } from '@/hooks/useComparatorSampleCollected'

export interface ComparatorRowProps {
  /** Side-nav group id used for anchors, keys, and removal identity. */
  groupId: string
  /** Display label for this comparator (e.g. "Sibling 1"). */
  groupLabel: string
  comparator: Comparator
  /** Whether this comparator is editable (drives Edit pencils + Remove visibility). */
  canEditRow: boolean
  /**
   * Whether removal is otherwise permitted for this order (pre-accession window
   * etc.). When false, the sample-collection gate is not consulted and removal
   * stays open — matching the prior page-level `canEditComparatorParents` guard.
   */
  canEditComparatorParents: boolean
  /** Submitted order id used by the per-row sample-collection lookup. */
  orderId?: string | null
  consentConfigs: ComparatorParentConsentConfigSlice
  mrnRequired?: boolean
  probandResearchContactConsent?: boolean
  onRemove: () => void
  onEditDemographics: () => void
  onEditClinical: () => void
  onEditConsent: () => void
}

/**
 * A single comparator section on the submitted-order page.
 *
 * Owns its OWN sample-collection gate (`useComparatorSampleCollected` keyed to
 * this comparator's `demographic.demographicId`) so the Remove button reflects
 * THIS comparator's status. Previously the page computed a single gate for the
 * scroll-selected comparator and applied it to every row, so scrolling (which
 * changes the selected comparator via the side-nav scroll-spy) flipped every
 * Remove button together. Computing per row decouples the button from scroll
 * position.
 */
export function ComparatorRow({
  groupId,
  groupLabel,
  comparator,
  canEditRow,
  canEditComparatorParents,
  orderId,
  consentConfigs,
  mrnRequired,
  probandResearchContactConsent,
  onRemove,
  onEditDemographics,
  onEditClinical,
  onEditConsent,
}: ComparatorRowProps) {
  const demo = comparator.demographic ?? {}
  const rowName = [demo.firstName, demo.lastName]
    .filter((p) => p && String(p).trim())
    .join(' ')
    .trim()

  const rowDemographicId = demo.demographicId?.trim() || ''
  const sampleCollectedGate = useComparatorSampleCollected({
    orderId,
    patientId: rowDemographicId,
    enabled: canEditComparatorParents && rowDemographicId.length > 0,
  })
  // Only gate removal when the check is actually running for this comparator
  // (i.e. we have a server-side identity to look it up by). When there is no
  // demographicId, there is nothing to collect against, so removal stays open.
  const removeBlockedBySampleCollection =
    canEditComparatorParents && rowDemographicId.length > 0 && !sampleCollectedGate.canRemove
  const removeBlockedReason =
    sampleCollectedGate.status === 'loading'
      ? 'Checking whether this comparator’s sample has been collected…'
      : sampleCollectedGate.status === 'error'
        ? 'We couldn’t confirm this comparator’s sample-collection status. Removal is unavailable until it can be verified.'
        : 'This comparator’s sample has been collected and can no longer be removed.'

  return (
    <div className="mt-2 flex flex-col gap-4 border-t border-border-base dark:border-gray-700 pt-6">
      <div className="sticky top-(--order-header-h) z-20 -mx-1 flex items-center gap-3 bg-background-variant-gray dark:bg-gray-950 px-1 pb-2 pt-4">
        <h2 className="text-xl font-semibold tracking-tight text-text-heading dark:text-white">
          {groupLabel}
          {rowName ? <span className="text-text-body-subtle dark:text-gray-500"> · {rowName}</span> : null}
        </h2>
        {canEditRow ? (
          removeBlockedBySampleCollection ? (
            <button
              type="button"
              disabled
              aria-disabled="true"
              aria-label={`Remove ${groupLabel}${rowName ? ` (${rowName})` : ''} (unavailable)`}
              title={removeBlockedReason}
              className="ml-auto inline-flex cursor-not-allowed items-center gap-1.5 rounded-full border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm font-medium text-text-disabled dark:text-gray-600 shadow-xs"
            >
              <TrashBin className="h-4 w-4" aria-hidden />
              Remove Comparator
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                if (removeBlockedBySampleCollection) return
                onRemove()
              }}
              aria-label={`Remove ${groupLabel}${rowName ? ` (${rowName})` : ''}`}
              className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-border-error-subtle dark:border-error-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm font-medium text-text-fg-error dark:text-error-100 shadow-xs transition-colors hover:bg-error-50 dark:hover:bg-gray-800"
            >
              <TrashBin className="h-4 w-4" aria-hidden />
              Remove Comparator
            </button>
          )
        ) : null}
      </div>
      {canEditRow && removeBlockedBySampleCollection && removeBlockedReason ? (
        <p className="text-xs font-medium text-text-body-subtle dark:text-gray-500">{removeBlockedReason}</p>
      ) : null}
      <ComparatorDetailView
        comparator={comparator}
        titleLabel={groupLabel}
        hideOwnHeader
        anchors={{
          demographics: `comparator-${groupId}-demographics`,
          clinical: `comparator-${groupId}-clinical`,
          consent: `comparator-${groupId}-consent`,
        }}
        consentConfigs={consentConfigs}
        onEditDemographics={canEditRow ? onEditDemographics : undefined}
        onEditClinical={canEditRow ? onEditClinical : undefined}
        onEditConsent={canEditRow ? onEditConsent : undefined}
        mrnRequired={mrnRequired}
        probandResearchContactConsent={probandResearchContactConsent}
      />
    </div>
  )
}
